/**
 * Sprint 10 Batch 1.4: Enhanced Trial Badge Component
 * Shows trial status in header/dashboard with click-to-action
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Clock, Zap, AlertTriangle, Crown, CalendarClock } from 'lucide-react';
import { usePackageCatalog } from '../context/PackageCatalogContext';

const TrialBadge = ({ 
  subscription, 
  trialStatus, 
  onClick,
  onShowWarning, // New: callback to show warning modal
  size = 'default' // 'default' | 'small' | 'large'
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getPackageById } = usePackageCatalog();
  
  if (!subscription) return null;
  
  const packageConfig = getPackageById(subscription.packageId);
  
  const sizeClasses = {
    small: 'text-xs px-2 py-0.5 gap-1',
    default: 'text-sm px-3 py-1 gap-1.5',
    large: 'text-base px-4 py-1.5 gap-2',
  };
  
  const iconSizes = {
    small: 'h-3 w-3',
    default: 'h-4 w-4',
    large: 'h-5 w-5',
  };
  
  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (subscription.packageId === 'TRIAL' && trialStatus) {
      // For trial users, show warning or navigate to upgrade
      if (trialStatus.isLastDay || trialStatus.expired) {
        onShowWarning?.();
      } else {
        navigate('/clinic/packages');
      }
    } else {
      navigate('/clinic/packages');
    }
  };
  
  // Paid subscription badge
  if (subscription.packageId !== 'TRIAL') {
    return (
      <div 
        className={`inline-flex items-center rounded-full font-medium cursor-pointer hover:opacity-80 transition-all ${packageConfig?.color || 'bg-gray-100 text-gray-800'} ${sizeClasses[size]}`}
        onClick={handleClick}
        data-testid="subscription-badge"
        title={t('badge.clickToManage', 'Click to manage subscription')}
      >
        <Crown className={iconSizes[size]} />
        <span>{packageConfig?.displayName || subscription.packageId}</span>
      </div>
    );
  }
  
  // Trial badge
  if (!trialStatus) return null;
  
  const { expired, currentDay, totalDays, extended, daysRemaining, isLastDay, isAlmostExpired } = trialStatus;
  
  // Expired trial - prominent red badge
  if (expired) {
    return (
      <div 
        className={`inline-flex items-center rounded-full font-medium cursor-pointer bg-red-500 text-white border-2 border-red-300 hover:bg-red-600 transition-all animate-pulse ${sizeClasses[size]}`}
        onClick={handleClick}
        data-testid="trial-badge-expired"
        title={t('badge.clickToUpgrade', 'Click to upgrade')}
      >
        <AlertTriangle className={iconSizes[size]} />
        <span>{t('trial.expired', 'Trial Expired')}</span>
      </div>
    );
  }
  
  // Last day - urgent amber/red badge with animation
  if (isLastDay) {
    return (
      <div 
        className={`inline-flex items-center rounded-full font-medium cursor-pointer bg-gradient-to-r from-amber-500 to-red-500 text-white border border-red-300 hover:from-amber-600 hover:to-red-600 transition-all shadow-lg ${sizeClasses[size]}`}
        onClick={handleClick}
        data-testid="trial-badge-last-day"
        title={t('badge.lastDayClick', 'Last day! Click to extend or upgrade')}
      >
        <CalendarClock className={`${iconSizes[size]} animate-bounce`} />
        <span>{t('trial.lastDay', 'Last Day!')}</span>
      </div>
    );
  }
  
  // Active trial - color-coded by urgency
  let badgeClasses = 'bg-blue-100 text-blue-800 border-blue-200';
  let Icon = Clock;
  
  if (daysRemaining <= 2) {
    badgeClasses = 'bg-amber-100 text-amber-800 border-amber-300';
    Icon = CalendarClock;
  }
  
  if (extended) {
    badgeClasses = 'bg-purple-100 text-purple-800 border-purple-200';
    Icon = Zap;
  }
  
  const prefix = extended 
    ? t('trial.extended', 'Extended Trial') 
    : t('trial.trial', 'Trial');
  const dayText = t('trial.dayOf', 'Day {{current}} of {{total}}', { 
    current: currentDay, 
    total: totalDays 
  });
  
  return (
    <div 
      className={`inline-flex items-center rounded-full font-medium cursor-pointer border hover:shadow-md transition-all ${badgeClasses} ${sizeClasses[size]}`}
      onClick={handleClick}
      data-testid="trial-badge"
      title={t('badge.clickToViewPlans', 'Click to view plans')}
    >
      <Icon className={iconSizes[size]} />
      <span>{prefix}: {dayText}</span>
      {daysRemaining <= 2 && !extended && (
        <span className="ml-1 text-xs opacity-75">
          {t('trial.extendAvailable', '(extend?)')}
        </span>
      )}
    </div>
  );
};

export default TrialBadge;
